import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import React, { FC, useCallback, useReducer, useState } from 'react';
import * as R from 'ramda';
import { action } from 'typesafe-actions';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { isDefined } from '@dxb-web-ui-framework/common/utils/Helper';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { ActivityLogServiceImpl } from '../../domain/ActivityLog/usecases';
import { ActivityLogRepositoryImpl } from '../../domain/ActivityLog/infrastructure';
import {
  STATUS_DEFAULT_VALUE,
  TYPE_DEFAULT_VALUE,
} from '../../components/ActivityLogTable/useActivityLogFiltersConfig';
import { useTransactionStatuses, useTransactionTypes } from '../../../../transaction/src/contexts/Parameters/hooks';
import { ActivityLogActionTypes, ActivityLogListFilters } from './types';
import { ActivityLogContext, ActivityLogDispatchContext } from './ActivityLogContext';
import { defaultState } from './defaultState';
import { ActivityLogReducer } from './ActivityLogReducer';

const getActivityLogService = () => {
  const activityLogItemRepo = new ActivityLogRepositoryImpl();
  return new ActivityLogServiceImpl(activityLogItemRepo);
};

const ActivityLogProvider: FC = ({ children }) => {
  // Get initial state values
  const [state, dispatch] = useReducer(ActivityLogReducer, defaultState);
  const customerId = useSelectedCustomerId();

  const [activityLogService] = useState<ActivityLogServiceImpl>(getActivityLogService());
  const statuses = useTransactionStatuses();
  const types = useTransactionTypes();

  // Define api call conditions
  const shouldFetchActivityLogList = !isEmpty(customerId) && Boolean(activityLogService);
  const shouldFetchActivityLogDetails = !isEmpty(state.selectedActivityLog);

  const {
    data: activityLogListData,
    error: activityLogListDataError,
    isLoading: isLoadingActivityLogList,
  } = useInvokeSWRService(
    activityLogService?.GetActivityLogList,
    {
      // TODO: remove them when filter functionality is ready.
      pageSize: state.activityLogListFilters.pageSize,
      pageOffset: state.activityLogListFilters.pageOffset,
    },
    shouldFetchActivityLogList,
    'activityLogService.GetActivityLogList',
  );

  const {
    data: activityLogDetailsData,
    error: activityLogDetailsError,
    isLoading: isLoadingActivityLogDetails,
  } = useInvokeSWRService(
    activityLogService?.GetActivityLogDetails,
    {
      id: state.selectedActivityLog?.id,
    },
    shouldFetchActivityLogDetails,
    'activityLogService.GetActivityLogDetails',
  );

  const resetSelectedActivityLog = useCallback(() => {
    dispatch(action(ActivityLogActionTypes.SetSelectedActivityLog, null));
  }, []);

  const onSelectActivityLog = useCallback(
    (activityLogId?: number) => {
      if (activityLogId && activityLogListData) {
        const selectedActivityLog = R.find(activityLog => activityLog.id === activityLogId, activityLogListData);
        dispatch(action(ActivityLogActionTypes.SetSelectedActivityLog, selectedActivityLog || null));
      } else {
        dispatch(action(ActivityLogActionTypes.SetSelectedActivityLog, null));
      }
    },
    [activityLogListData],
  );

  // toggle the activity log filter drawer
  const toggleActivityLogFilterDrawer = useCallback(() => {
    dispatch(action(ActivityLogActionTypes.SetIsActivityFilterDrawerOpen, !state.isActivityLogFilterDrawerOpen));
  }, [state.isActivityLogFilterDrawerOpen]);

  const modifyActivityLogDataFilterDrawer = (filters: Partial<ActivityLogListFilters>) => {
    if (filters.dateUpdated ?? false) {
      filters.fromDate = filters.dateUpdated?.[0];
      filters.toDate = filters.dateUpdated?.[1];
    }

    if (filters?.type === TYPE_DEFAULT_VALUE) {
      delete filters.type;
      delete filters.typeDescription;
    }

    if (filters?.status === STATUS_DEFAULT_VALUE) {
      delete filters.status;
      delete filters.statusDescription;
    }

    if (!isDefined(filters.account)) {
      delete filters.account;
    }

    delete filters.dateUpdated;
  };

  const onActivityLogListFiltersChange = useCallback(
    (
      filters: Partial<ActivityLogListFilters>,
      toToggleFilterDrawer: boolean = true,
      nextPage: number = 1,
      totalPages: number = 1,
    ) => {
      modifyActivityLogDataFilterDrawer(filters);
      toToggleFilterDrawer && toggleActivityLogFilterDrawer();
      const newFilters = [
        {
          key: 'fromDate',
          value: filters?.fromDate
            ? stringToDate({ date: filters.fromDate, formatExp: "yyyy-MM-dd'T'00:00:00" })
            : null,
        },
        {
          key: 'toDate',
          value: filters?.toDate ? stringToDate({ date: filters.toDate, formatExp: "yyyy-MM-dd'T'00:00:00" }) : null,
        },
        { key: 'status', value: filters?.status },
        {
          key: 'statusDescription',
          value: statuses?.find(({ id }) => id === filters?.status),
        },
        { key: 'type', value: filters?.type },
        {
          key: 'typeDescription',
          value: types?.find(({ id }) => id === filters?.type),
        },
        { key: 'account', value: filters?.account },
      ].reduce((acc, { key, value }) => {
        if (value?.toString()) {
          acc[key] = value;
        }
        return acc;
      }, {} as GenericObject);
      dispatch(
        action(ActivityLogActionTypes.SetActivityLogListFilter, {
          ...newFilters,
          pageOffset: filters.pageSize
            ? Math.min(Math.max(nextPage - 1, 0), totalPages) * Math.max(filters.pageSize ?? 1, 1)
            : 0,
          pageSize: filters.pageSize ? state.activityLogListFilters.pageSize : 10,
        }),
      );
    },
    [state.activityLogListFilters.pageSize, toggleActivityLogFilterDrawer, statuses, types],
  );

  return (
    <ActivityLogContext.Provider
      value={{
        ...state,
        activityLogListData,
        activityLogDetailsData,
        errorState: {
          activityLogListDataError,
          activityLogDetailsError,
        },
        loadingState: {
          isLoadingActivityLogList,
          isLoadingActivityLogDetails,
        },
        onSelectActivityLog,
        resetSelectedActivityLog,
        onActivityLogListFiltersChange,
        toggleActivityLogFilterDrawer,
      }}>
      <ActivityLogDispatchContext.Provider value={dispatch}>{children}</ActivityLogDispatchContext.Provider>
    </ActivityLogContext.Provider>
  );
};

export { ActivityLogProvider };
